import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

const ApproveModal = ({
	isOpen,
	onClose,
	title,
	message = '',
	billStatus,
	onYesClick,
	newStatus,
	onRejectClick,
	customClass = '',
	loading,
	data,
	retrictedAmount,
}) => {
	const [remark, setRemark] = useState('');
	const [error, setError] = useState('');
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

	// Same 60%-of-selling-price cap the backend re-validates on approval
	// (models/tripAdvanceModel.js, updatetripadvancestatus) — shown here so
	// approving something already known to be over the cap doesn't have to
	// round-trip to the server just to find out it'll be rejected.
	//
	// retrictedAmount (data.restrict_amount) is a GROUP-level total: cap minus
	// the sum of EVERY non-rejected request for this booking+vendor, which
	// already includes this specific item's own amount. Comparing this item's
	// amount against that already-inclusive number is wrong — e.g. two ₹1
	// requests against a ₹1 cap: restrict_amount = 1 - 2 = -1, but neither
	// request's OWN amount (₹1) exceeds -1 in the naive sense; the real
	// question is just whether the group total already exceeds the cap at
	// all (algebraically equivalent for any item in the group, not
	// item-specific), so this only needs to check whether the group-level
	// figure itself is negative.
	const requestedAmount = Number(data) || 0;
	const cap = retrictedAmount === undefined || retrictedAmount === null
		? null
		: Number(retrictedAmount);
	const overCap = cap !== null && cap < 0;

	useEffect(() => {
		if (!isOpen) {
			setRemark('');
			setError('');
		}
	}, [isOpen]);

	const handleReject = () => {
		// Open the remark modal
		setIsRejectModalOpen(true);
	};

	const handleApprove = () => {
		setError('');
		onYesClick(); // Approve action
	};

	const handleRejectSubmit = () => {
		if (!remark.trim()) {
			setError('Remark is required!');
			return;
		}
		setError('');
		onRejectClick(remark); // Pass the remark to the onRejectClick handler
		setIsRejectModalOpen(false); // Close the remark modal
	};

	const handleRejectClose = () => {
		setIsRejectModalOpen(false); // Close the reject modal without submitting
	};

	return (
		<div>
			{/* Main Modal */}
			<Modal
				open={isOpen}
				onClose={onClose}
				aria-labelledby="modal-title"
				aria-describedby="modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						width: '400px',
						borderRadius: '8px',
					}}
				>
					{/* Modal Header */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: 2,
						}}
					>
						<Typography
							variant="h6"
							id="modal-title"
							sx={{ textAlign: 'left' }}
						>
							{title}
						</Typography>
						<button
							onClick={onClose}
							className="text-gray-500 hover:text-gray-800 transition duration-200"
							style={{
								background: 'transparent',
								border: 'none',
								cursor: 'pointer',
							}}
							aria-label="Close"
						>
							✕
						</button>
					</Box>

					{/* Modal Body */}
					<Box sx={{ textAlign: 'center', marginBottom: 2 }}>
						{message && (
							<Typography sx={{ color: 'text.secondary' }}>
								{message}
							</Typography>
						)}
					</Box>
					{billStatus === 'loss' && (
						<p className="text-red-500 mt-2">
							Cannot approve, the bill amount is a loss.
						</p>
					)}
					{overCap && (
						<p className="text-red-500 mt-2">
							Cannot approve — this booking's total requested (₹
							{requestedAmount} for this request alone) already exceeds
							the 60%-of-selling-price cap by ₹{Math.abs(cap)}. Reject
							enough of the other pending requests first to bring the
							total back within the limit.
						</p>
					)}
					{/* Modal Actions */}
					<Box
						sx={{ display: 'flex', justifyContent: 'center', padding: '30px' }}
					>
						{loading ? (
							<CircularProgress size={24} />
						) : (
							<>
								<Button
									variant="text"
									onClick={handleReject}
									disabled={loading}
									sx={{
										color: 'red',
										marginRight: 2,
									}}
								>
									Reject
								</Button>
								<Button
									variant="text"
									onClick={handleApprove}
									sx={{
										color: 'green',
										marginRight: 2,
									}}
									disabled={billStatus === 'loss' || overCap || loading}
								>
									Approve
								</Button>
							</>
						)}
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal for Entering Remark */}
			<Modal
				open={isRejectModalOpen}
				onClose={handleRejectClose}
				aria-labelledby="reject-modal-title"
				aria-describedby="reject-modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						width: '400px',
						borderRadius: '8px',
					}}
				>
					{/* Reject Modal Header */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: 2,
						}}
					>
						<Typography
							variant="h6"
							id="reject-modal-title"
							sx={{ textAlign: 'left' }}
						>
							Tell us know the reason
						</Typography>
						<button
							onClick={handleRejectClose}
							className="text-gray-500 hover:text-gray-800 transition duration-200"
							style={{
								background: 'transparent',
								border: 'none',
								cursor: 'pointer',
							}}
							aria-label="Close"
						>
							✕
						</button>
					</Box>

					{/* Reject Modal Body */}
					<Box sx={{ textAlign: 'center', marginBottom: 2 }}>
						<TextField
							value={remark}
							inputProps={{ maxLength: 200 }}
							onChange={(e) => setRemark(e.target.value)}
							label="Reason for reject"
							fullWidth
							variant="outlined"
							error={Boolean(error)}
							helperText={error}
							sx={{ marginTop: 2 }}
						/>
					</Box>

					{/* Reject Modal Actions */}
					<Box sx={{ display: 'flex', justifyContent: 'end' }}>
						<Button
							variant="text"
							onClick={handleRejectClose}
							sx={{
								color: 'red',
								marginRight: 2,
							}}
						>
							Cancel
						</Button>
						<Button
							variant="text"
							onClick={handleRejectSubmit}
							sx={{
								color: 'green',
								marginRight: 2,
							}}
						>
							Submit
						</Button>
					</Box>
				</Box>
			</Modal>
		</div>
	);
};

export default React.memo(ApproveModal);
